import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { companyInfo } from '../../utils/dataInfo';

// Helper function to format currency
const formatCurrency = (amount) => {
    if (!amount) return '0.00';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount) + " "+"sh";
};

// Helper function to format number with commas
const formatNumberWithComma = (number) => {
    if (!number) return '0';
    return new Intl.NumberFormat('en-US').format(number);
};

// Helper function to get status name
const getStatusName = (status) => {
    const statusMap = {
        'draft': 'Draft',
        'submitted': 'Submitted',
        'approved': 'Approved',
        'rejected': 'Rejected'
    };
    return statusMap[status] || status;
};

// Helper function to get branch name
const getBranchName = (branchId, branchNamesData) => {
    if (!branchNamesData || !Array.isArray(branchNamesData)) return 'Unknown';
    
    // Try different ways to find the branch
    const branch = branchNamesData.find(b => {
        if (!branchId) return false;
        // Try exact match first
        if (b.id == branchId) return true;
        // Try string comparison
        if (String(b.id) === String(branchId)) return true;
        // Try integer comparison
        if (parseInt(b.id) === parseInt(branchId)) return true;
        return false;
    });
    
    return branch ? `${branch.name}` : 'Unknown';
};

// Helper function to get user name
const getUserName = (user) => {
    if (!user) return 'Unknown';
    return user.name || user.username || 'Unknown';
};

// Excel export for Day Clossings
export const exportToExcel = (clossings, dataSources = {}) => {
    try {
        const { branchNamesFilterData } = dataSources;
        
        // Prepare data for Excel
        const excelData = clossings.map((clossing, index) => {
            return {
                'No.': index + 1,
                'Clossing Number': clossing.clossing_number,
                'Date': new Date(clossing.clossing_date).toLocaleDateString(),
                'Branch': getBranchName(clossing.branch_id, branchNamesFilterData),
                'Opening Balance': formatCurrency(clossing.opening_balance),
                'Sales Total': formatCurrency(clossing.sales_total),
                'Clossing Balance': formatCurrency(clossing.clossing_balance),
                'Expected Cash': formatCurrency(clossing.expected_cash),
                'Actual Cash': formatCurrency(clossing.actual_cash),
                'Variance': formatCurrency(clossing.variance),
                'Variance %': clossing.expected_cash > 0 
                    ? `${((clossing.variance / clossing.expected_cash) * 100).toFixed(2)}%`
                    : '0%',
                'Status': getStatusName(clossing.status),
                'Prepared By': getUserName(clossing.user),
                'Notes': clossing.notes || '',
                'Created Date': new Date(clossing.created_at).toLocaleString(),
                'Updated Date': new Date(clossing.updated_at).toLocaleString(),
                'Item Count': clossing.items?.length || 0,
                'Total Items Sold': clossing.items?.reduce((sum, item) => sum + (item.sold_quantity || 0), 0) || 0
            };
        });

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Auto-size columns
        const maxWidth = excelData.reduce((widths, row) => {
            Object.keys(row).forEach((key, idx) => {
                const length = String(row[key]).length;
                if (!widths[idx] || length > widths[idx]) {
                    widths[idx] = length;
                }
            });
            return widths;
        }, []);
        
        worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Day Clossings');

        // Generate Excel file and download
        const fileName = `day_clossing_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Day Clossing Excel export failed:', error);
        throw new Error('Failed to export to Excel: ' + error.message);
    }
};

export const exportToPDF = (clossings, dataSources = {}) => {
    try {
        const { branchNamesFilterData } = dataSources;
        
        // Create PDF in landscape mode
        const doc = new jsPDF('landscape');
        
        // Set document properties
        doc.setProperties({
            title: 'Day Clossing Report',
            subject: 'Day Clossing Export',
            author: companyInfo.name,
            keywords: 'day clossing, cash, sales, inventory, report',
            creator: companyInfo.name
        });
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('DAY CLOSSING REPORT', 148, 20, { align: 'center' });
        
        // Company info
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        doc.text(`Company: ${companyInfo.name}`, 14, 35);
        doc.text(`Address: ${companyInfo.address}`, 14, 40);
        doc.text(`Phone: ${companyInfo.phone}`, 14, 45);
        doc.text(`Email: ${companyInfo.email}`, 14, 50);
        
        // Generation details
        doc.setFontSize(10);
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, 60);
        doc.text(`Total Clossings: ${clossings.length}`, 14, 65);
        doc.text(`Date Range: All records`, 14, 70);
        
        // Branch information if available
        if (clossings.length > 0 && clossings[0].branch_id) {
            const branchName = getBranchName(clossings[0].branch_id, branchNamesFilterData);
            if (branchName !== 'Unknown') {
                doc.text(`Branch: ${branchName}`, 14, 75);
            }
        }
        
        let currentY = 85;
        
        // Prepare table data - REMOVED 'Var %' COLUMN
        const tableData = clossings.map((clossing, index) => {
            return [
                (index + 1).toString(),
                clossing.clossing_number,
                new Date(clossing.clossing_date).toLocaleDateString(),
                getBranchName(clossing.branch_id, branchNamesFilterData),
                formatCurrency(clossing.opening_balance),
                formatCurrency(clossing.sales_total),
                formatCurrency(clossing.clossing_balance),
                formatCurrency(clossing.expected_cash),
                formatCurrency(clossing.actual_cash),
                formatCurrency(clossing.variance),
                getStatusName(clossing.status),
                getUserName(clossing.user)
            ];
        });
        
        // Define table columns - REMOVED 'Var %' HEADER
        const headers = [
            ['#', 'Clossing No', 'Date', 'Branch', 'Opening Bal', 'Sales Total', 'Clossing Bal', 
            'Expected Cash', 'Actual Cash', 'Variance', 'Status', 'Prepared By']
        ];
        
        // Add table using autoTable - UPDATED COLUMN STYLES
        autoTable(doc, {
            head: headers,
            body: tableData,
            startY: currentY,
            theme: 'striped',
            headStyles: { 
                fillColor: [25, 118, 210],
                textColor: [255, 255, 255],
                fontStyle: 'bold'
            },
            alternateRowStyles: {
                fillColor: [248, 248, 248]
            },
            columnStyles: {
                0: { cellWidth: 10 },
                1: { cellWidth: 25 },
                2: { cellWidth: 25 },
                3: { cellWidth: 30 },
                4: { cellWidth: 25 },
                5: { cellWidth: 25 },
                6: { cellWidth: 25 },
                7: { cellWidth: 25 },
                8: { cellWidth: 25 },
                9: { cellWidth: 25 },
                10: { cellWidth: 25 },
                11: { cellWidth: 30 }
            },
            margin: { left: 14, right: 14 },
            didDrawPage: function(data) {
                // Add page numbers
                const pageCount = doc.internal.getNumberOfPages();
                doc.setFontSize(10);
                doc.text(
                    `Page ${data.pageNumber} of ${pageCount}`,
                    doc.internal.pageSize.width / 2,
                    doc.internal.pageSize.height - 10,
                    { align: 'center' }
                );
            }
        });
        
        const fileName = `day_clossing_report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Day Clossing PDF export failed:', error);
        throw new Error('Failed to export to PDF: ' + error.message);
    }
};
// Detailed PDF export with items - UPDATED TO LANDSCAPE MODE
export const exportDetailedPDF = (clossings, dataSources = {}) => {
    try {
        const { branchNamesFilterData } = dataSources;
        
        // Create PDF in landscape mode
        const doc = new jsPDF('landscape');
        
        // Set document properties
        doc.setProperties({
            title: 'Day Clossing Detailed Report',
            subject: 'Day Clossing Detailed Export',
            author: companyInfo.name,
            keywords: 'day clossing, detailed, cash, sales, inventory, items, report',
            creator: companyInfo.name
        });
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('DAY CLOSSING DETAILED REPORT', 148, 20, { align: 'center' });
        
        // Company info
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        doc.text(`Company: ${companyInfo.name}`, 14, 35);
        doc.text(`Address: ${companyInfo.address}`, 14, 40);
        doc.text(`Phone: ${companyInfo.phone}`, 14, 45);
        doc.text(`Email: ${companyInfo.email}`, 14, 50);
        
        // Generation details
        doc.setFontSize(10);
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, 60);
        doc.text(`Total Clossings: ${clossings.length}`, 14, 65);
        
        let currentY = 75;
        
        clossings.forEach((clossing, index) => {
            // Check if we need a new page
            if (currentY > 150 && index > 0) {
                doc.addPage('landscape');
                currentY = 20;
            }
            
            // Clossing header
            doc.setFontSize(14);
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'bold');
            doc.text(`Clossing: ${clossing.clossing_number}`, 14, currentY);
            
            // Clossing details
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            currentY += 10;
            doc.text(`Date: ${new Date(clossing.clossing_date).toLocaleDateString()}`, 14, currentY);
            doc.text(`Branch: ${getBranchName(clossing.branch_id, branchNamesFilterData)}`, 100, currentY);
            currentY += 6;
            doc.text(`Status: ${getStatusName(clossing.status)}`, 14, currentY);
            doc.text(`Prepared By: ${getUserName(clossing.user)}`, 100, currentY);
            currentY += 6;
            doc.text(`Notes: ${clossing.notes || 'None'}`, 14, currentY);
            currentY += 10;
            
            // Financial summary
            doc.setFont(undefined, 'bold');
            doc.text('Financial Summary:', 14, currentY);
            doc.setFont(undefined, 'normal');
            currentY += 7;
            doc.text(`Opening Balance: ${formatCurrency(clossing.opening_balance)}`, 20, currentY);
            doc.text(`Expected Cash: ${formatCurrency(clossing.expected_cash)}`, 120, currentY);
            currentY += 6;
            doc.text(`Sales Total: ${formatCurrency(clossing.sales_total)}`, 20, currentY);
            doc.text(`Actual Cash: ${formatCurrency(clossing.actual_cash)}`, 120, currentY);
            currentY += 6;
            doc.text(`Clossing Balance: ${formatCurrency(clossing.clossing_balance)}`, 20, currentY);
            doc.text(`Variance: ${formatCurrency(clossing.variance)}`, 120, currentY);
            currentY += 6;
            if (clossing.expected_cash > 0) {
                doc.text(`Variance %: ${((clossing.variance / clossing.expected_cash) * 100).toFixed(2)}%`, 120, currentY);
                currentY += 6;
            }
            currentY += 10;
            
            // Items table if available
            if (clossing.items && clossing.items.length > 0) {
                doc.setFont(undefined, 'bold');
                doc.text('Stock Items:', 14, currentY);
                currentY += 7;
                
                const itemHeaders = [
                    ['Item', 'Opening Qty', 'Sold Qty', 'Clossing Qty', 'Opening Value', 'Sales Value', 'Clossing Value']
                ];
                
                const itemData = clossing.items.map((item, itemIndex) => {
                    const stockName = item.stock?.name || 'Unknown';
                    return [
                        stockName,
                        formatNumberWithComma(item.opening_quantity),
                        formatNumberWithComma(item.sold_quantity),
                        formatNumberWithComma(item.clossing_quantity),
                        formatCurrency(item.opening_value),
                        formatCurrency(item.sales_value),
                        formatCurrency(item.clossing_value)
                    ];
                });
                
                autoTable(doc, {
                    head: itemHeaders,
                    body: itemData,
                    startY: currentY,
                    theme: 'striped',
                    headStyles: { 
                        fillColor: [76, 175, 80],
                        textColor: [255, 255, 255],
                        fontStyle: 'bold'
                    },
                    alternateRowStyles: {
                        fillColor: [248, 248, 248]
                    },
                    columnStyles: {
                        0: { cellWidth: 50 },
                        1: { cellWidth: 25 },
                        2: { cellWidth: 25 },
                        3: { cellWidth: 25 },
                        4: { cellWidth: 35 },
                        5: { cellWidth: 35 },
                        6: { cellWidth: 35 }
                    },
                    margin: { left: 14, right: 14 }
                });
                
                currentY = doc.lastAutoTable.finalY + 15;
            } else {
                currentY += 10;
            }
            
            // Add separator between clossings
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.5);
            doc.line(14, currentY, 280, currentY);
            currentY += 15;
        });
        
        // Add page numbers
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(10);
            doc.text(
                `Page ${i} of ${pageCount}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 10,
                { align: 'center' }
            );
        }
        
        const fileName = `day_clossing_detailed_report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Day Clossing Detailed PDF export failed:', error);
        throw new Error('Failed to export detailed PDF: ' + error.message);
    }
};

// CSV export for Day Clossings
export const exportToCSV = (clossings, dataSources = {}) => {
    try {
        const { branchNamesFilterData } = dataSources;
        
        const headers = [
            'Clossing Number',
            'Date',
            'Branch',
            'Opening Balance',
            'Sales Total',
            'Clossing Balance',
            'Expected Cash',
            'Actual Cash',
            'Variance',
            'Variance %',
            'Status',
            'Prepared By',
            'Notes',
            'Created Date',
            'Item Count',
            'Total Items Sold'
        ];
        
        const csvData = clossings.map(clossing => {
            const variancePercent = clossing.expected_cash > 0 
                ? ((clossing.variance / clossing.expected_cash) * 100).toFixed(2)
                : '0';
            
            const totalItemsSold = clossing.items?.reduce((sum, item) => sum + (item.sold_quantity || 0), 0) || 0;
            
            return [
                `"${clossing.clossing_number.replace(/"/g, '""')}"`,
                `"${new Date(clossing.clossing_date).toLocaleDateString().replace(/"/g, '""')}"`,
                `"${getBranchName(clossing.branch_id, branchNamesFilterData).replace(/"/g, '""')}"`,
                formatCurrency(clossing.opening_balance),
                formatCurrency(clossing.sales_total),
                formatCurrency(clossing.clossing_balance),
                formatCurrency(clossing.expected_cash),
                formatCurrency(clossing.actual_cash),
                formatCurrency(clossing.variance),
                `${variancePercent}%`,
                `"${getStatusName(clossing.status).replace(/"/g, '""')}"`,
                `"${getUserName(clossing.user).replace(/"/g, '""')}"`,
                `"${(clossing.notes || '').replace(/"/g, '""')}"`,
                `"${new Date(clossing.created_at).toLocaleString().replace(/"/g, '""')}"`,
                clossing.items?.length || 0,
                totalItemsSold
            ];
        });

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `day_clossing_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return { success: true };
    } catch (error) {
        console.error('Day Clossing CSV export failed:', error);
        throw new Error('Failed to export to CSV: ' + error.message);
    }
};

// Simple PDF export (for reference only - keeping as minimal export)
export const exportDayClossingSimplePDF = (clossings, dataSources = {}) => {
    try {
        const { branchNamesFilterData } = dataSources;
        
        // Create PDF in landscape mode
        const doc = new jsPDF('landscape');
        
        // Simple title
        doc.setFontSize(16);
        doc.text('Day Clossing Report', 20, 20);
        
        // Simple info
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 30);
        doc.text(`Total: ${clossings.length} clossings`, 20, 37);
        
        // Show branch information if available
        if (clossings.length > 0 && clossings[0].branch_id) {
            const branchName = getBranchName(clossings[0].branch_id, branchNamesFilterData);
            if (branchName !== 'Unknown') {
                doc.text(`Branch: ${branchName}`, 20, 44);
            }
        }
        
        let y = 55;
        
        clossings.forEach((clossing, index) => {
            if (y > 150) {
                doc.addPage('landscape');
                y = 20;
            }
            
            doc.setFontSize(11);
            doc.text(`${index + 1}. ${clossing.clossing_number}`, 20, y);
            doc.setFontSize(10);
            doc.text(`Date: ${new Date(clossing.clossing_date).toLocaleDateString()}`, 20, y + 6);
            doc.text(`Branch: ${getBranchName(clossing.branch_id, branchNamesFilterData)}`, 100, y + 6);
            doc.text(`Sales: ${formatCurrency(clossing.sales_total)}`, 20, y + 12);
            doc.text(`Cash: ${formatCurrency(clossing.actual_cash)}`, 100, y + 12);
            doc.text(`Variance: ${formatCurrency(clossing.variance)}`, 180, y + 12);
            doc.text(`Status: ${getStatusName(clossing.status)}`, 20, y + 18);
            
            y += 30;
        });
        
        const fileName = `day_clossing_simple_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Day Clossing simple PDF export failed:', error);
        throw new Error('Failed to export simple PDF: ' + error.message);
    }
};